import { Injectable } from '@angular/core';
import {
	HttpErrorResponse, HttpInterceptor, HttpRequest, HttpHandler, HttpEvent
} from '@angular/common/http';
import { Router, NavigationExtras } from '@angular/router';
import { from, Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { TokenService } from '../services/TokenService';
import { UserService } from '../services/UserService';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
	constructor(
		private router: Router,
		private tokenService: TokenService,
		private userService: UserService,
	) {}

	intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		// Skip auth handling for unauthenticated endpoints, the refresh endpoint, and static assets
		if (req.url.includes('/auth/refresh') || req.url.includes('/unauth/') || req.url.includes('/assets/')) {
			return next.handle(req).pipe(catchError((err) => this.handleError(err)));
		}

		if (this.tokenService.isExpiringSoon()) {
			return from(this.tokenService.refresh()).pipe(
				switchMap((tokens) => next.handle(this.addAuth(req, tokens.idToken))),
				catchError((err) => this.handleError(err)),
			);
		}

		const tokens = this.tokenService.getTokens();
		if (tokens) {
			return next.handle(this.addAuth(req, tokens.idToken)).pipe(
				catchError((err) => this.handleError(err)),
			);
		}

		return next.handle(req).pipe(catchError((err) => this.handleError(err)));
	}

	private addAuth(req: HttpRequest<any>, idToken: string): HttpRequest<any> {
		const orgId = this.userService.user?.organizationId ?? '';
		return req.clone({
			headers: req.headers
				.set('Authorization', `Bearer ${idToken}`)
				.set('user-org-id', orgId),
		});
	}

	private handleError(err: any): Observable<never> {
		if (err instanceof HttpErrorResponse) {
			let errMessage: string;
			let route: string;

			if (err.status === 401) {
				if (!this.router.url.startsWith('/login')) {
					this.userService.detouredRoute = this.router.url;
					this.userService.logout();
					errMessage = 'unauthorized';
					route = '/login';
				}
			} else if (err.status === 404) {
				errMessage = 'missing';
				route = '/missing';
			} else if (err.status > 499 && err.status < 600) {
				errMessage = 'error';
			}

			if (route) {
				const navigationExtras: NavigationExtras = { state: { logoutMessage: errMessage } };
				this.router.navigateByUrl(route, navigationExtras);
			}
		}
		return throwError(() => err);
	}
}
